import React from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  Box,
  Grid,
  IconButton,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';

import {
  Settings as SettingsIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';

import {
  getShortName,
  getSubjectColorNew,
  getSubjectIconName,
} from '../../../../../helper';
import { ITrialClass } from '../../../../../store/V2/TrialsSlice';
import { TP_COLORS } from '../../../../../constants';

const gridStyles: SxProps = {
  backgroundColor: '#F8F9FD',
  marginBottom: '10px',
  alignItems: 'center',
  borderRadius: '10px',
  padding: '10px',
};

const imgStyles = {
  marginRight: '10px',
  height: '50px',
  borderRadius: '30px',
};

export type TActionTypes = 'edit' | 'status';

interface AdminTrialClassListItemProps {
  trialData: ITrialClass;
  handleActions: (action: TActionTypes, trialData: ITrialClass) => void;
}

const AdminTrialClassListItem: React.FC<AdminTrialClassListItemProps> = ({
  trialData,
  handleActions,
}) => {
  const navigate = useNavigate();

  const { course, dateTime, duration, student, tutor } = trialData;
  const subject = course?.subject || '-';
  const subjectColor = getSubjectColorNew(subject);
  const momentDate = moment(dateTime);
  const formattedDate = momentDate.format('DD MMM YYYY');
  const startTime = momentDate.format('hh:mm A');
  const endTime = momentDate.add(duration, 'minutes').format('hh:mm A');

  return (
    <Grid container sx={gridStyles}>
      <Grid item xs={6} sm={4} md={3.5}>
        <Box display="flex">
          <img
            src={`${
              process.env.PUBLIC_URL
            }/images/landingPage/icon-${getSubjectIconName(subject)}.avif`}
            alt="subject-img"
            style={{
              ...imgStyles,
              backgroundColor: `${subjectColor}33`,
            }}
          />
          <Stack justifyContent="center">
            <Typography
              variant="subtitle2"
              className="ellipsis-text"
              color={subjectColor}
            >
              {subject}
            </Typography>
            <Typography
              fontSize="12px"
              className="ellipsis-text"
              color={TP_COLORS.gray.darken}
            >
              {course?.curriculum}
            </Typography>
          </Stack>
        </Box>
      </Grid>
      <Grid item xs={6} sm={4} md={2}>
        <Typography variant="subtitle2" color={TP_COLORS.gray.darken}>
          {formattedDate}
        </Typography>
        <Typography fontSize="12px" className="ellipsis-text" color="#647488">
          {startTime} - {endTime}
        </Typography>
      </Grid>
      <Grid item xs={6} sm={2} md={1.5}>
        <Typography variant="subtitle2" color={TP_COLORS.gray.darken}>
          Tutor
        </Typography>
        <Typography fontSize="12px" className="ellipsis-text" color="#647488">
          {tutor ? getShortName(tutor.firstname, tutor.lastname) : '-'}
        </Typography>
      </Grid>
      <Grid item xs={6} sm={2} md={1.5}>
        <Typography variant="subtitle2" color={TP_COLORS.gray.darken}>
          Student
        </Typography>
        <Typography fontSize="12px" className="ellipsis-text" color="#647488">
          {student ? getShortName(student.firstname, student.lastname) : '-'}
        </Typography>
      </Grid>

      <Grid item xs={6} sm={9} md={1.5}>
        <Typography fontSize="12px" fontWeight="600" color="#647488">
          {trialData.status}
        </Typography>
      </Grid>
      <Grid
        item
        xs={6}
        sm={3}
        md={2}
        display="flex"
        justifyContent="space-evenly"
        alignItems="center"
      >
        <IconButton
          onClick={() =>
            navigate(`${process.env.PUBLIC_URL}/portal/courses/${course._id}`)
          }
        >
          <VisibilityIcon />
        </IconButton>
        <IconButton onClick={() => handleActions('edit', trialData)}>
          <EditIcon />
        </IconButton>
        <IconButton onClick={() => handleActions('status', trialData)}>
          <SettingsIcon />
        </IconButton>
      </Grid>
    </Grid>
  );
};

export default AdminTrialClassListItem;
